import { resolveEtlNodeFromDocument } from "./facet-etl-document-helpers";

/** Default when `decimal_places` is missing from facet ETL JSON. */
export const FACET_ETL_DEFAULT_DECIMAL_PLACES = 2;

/** Use nested `facet_result` when the execute API wraps the table payload. */
function facetResultRootFromExecuteResponse(data: Record<string, unknown>): Record<string, unknown> {
    const nested = data.facet_result;
    if (nested != null && typeof nested === "object" && !Array.isArray(nested)) {
        return nested as Record<string, unknown>;
    }
    return data;
}

/**
 * Keeps the same facet_result shape as GET facet (e.g. col_header, row_headers, op_doc_counts).
 * Syncs `headers` with `col_header` so chart/table helpers still resolve column names.
 */
export function facetResultFromExecuteResponse(data: Record<string, unknown>): Record<string, unknown> {
    const root = facetResultRootFromExecuteResponse(data);
    const out: Record<string, unknown> = { ...root, rows: Array.isArray(root.rows) ? root.rows : [] };

    const fromCol = Array.isArray(out.col_header) ? out.col_header : [];
    const fromHdr = Array.isArray(out.headers) ? out.headers : [];
    const labels = fromCol.length > 0 ? fromCol : fromHdr;
    if (labels.length > 0) {
        out.col_header = labels;
        out.headers = labels;
    }

    return out;
}

export function normalizationResultFromExecuteResponse(
    data: Record<string, unknown>
): Record<string, unknown> | undefined {
    const raw = data.normalization_result;
    if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return undefined;
    return raw as Record<string, unknown>;
}

/** Payload for `facet_execute_etl` from wrapped or bare facet ETL document. */
export function facetEtlExecuteParams(
    parsed: Record<string, unknown>
): { etl_json: Record<string, unknown>; decimal_places: number } | null {
    const etlNode = resolveEtlNodeFromDocument(parsed);
    if (!etlNode) return null;
    const raw = parsed.decimal_places;
    const decimal_places =
        typeof raw === "number" && Number.isFinite(raw) ? raw : FACET_ETL_DEFAULT_DECIMAL_PLACES;
    return { etl_json: parsed, decimal_places };
}

export function facetServerVersionFromUpdateResponse(resp: unknown): number | undefined {
    if (resp == null || typeof resp !== "object") return undefined;
    const v = (resp as Record<string, unknown>).version;
    return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

/** Normalized `facet_result` from facets_update / facets_get style payloads. */
export function facetResultFromUpdateResponse(resp: unknown): Record<string, unknown> | undefined {
    if (resp == null || typeof resp !== "object") return undefined;
    const normalized = facetResultFromExecuteResponse(resp as Record<string, unknown>);
    const hasHeaders =
        (Array.isArray(normalized.col_header) && normalized.col_header.length > 0) ||
        (Array.isArray(normalized.headers) && normalized.headers.length > 0);
    return hasHeaders ? normalized : undefined;
}
